import { BehaviorSubject } from 'rxjs';
import { filter, map, shareReplay, takeWhile, distinctUntilKeyChanged } from 'rxjs/operators';
import { invariant } from '@streem/toolbox';
import { logger } from '../../util/logger';
import { findInGroup, accumulateAndMapWallEvents } from '../wall/utils';
export class ARController {
    constructor(config) {
        this.config = config;
        this.localArWallItems = new Set();
        this.haveTrackables = new BehaviorSubject(false);
        this.source = this.config.roomWall.itemsByType.pipe(findInGroup('arSource'), accumulateAndMapWallEvents('arSource'), shareReplay(1));
        this.sessionStatus = this.config.roomWall.itemsByType.pipe(findInGroup('arSessionStatus'), accumulateAndMapWallEvents('arSessionStatus'), shareReplay(1));
        this.scanningRequest = this.config.roomWall.itemsByType.pipe(findInGroup('arScanningRequest'), accumulateAndMapWallEvents('arScanningRequest'), shareReplay(1));
        this.scanningStatus = this.config.roomWall.itemsByType.pipe(findInGroup('arScanningStatus'), accumulateAndMapWallEvents('arScanningStatus'), shareReplay(1));
        this.trackables = this.config.mediaController.data.messages.pipe(filter(msg => msg.type === 'arTrackables' && 'arTrackables' in msg), map(msg => msg.arTrackables?.trackables || []));
        this.trackableStatus = this.config.roomWall.itemsByType.pipe(findInGroup('arTrackableStatus'), accumulateAndMapWallEvents('arTrackableStatus'), shareReplay(1));
        this.lightEstimate = this.config.mediaController.data.messages.pipe(filter(msg => msg.type === 'arLightEstimate' && 'arLightEstimate' in msg), map(msg => msg.arLightEstimate || undefined));
        this.anchorRequest = this.config.roomWall.itemsByType.pipe(findInGroup('arAnchorRequest'), accumulateAndMapWallEvents('arAnchorRequest'), shareReplay(1));
        this.anchorStatus = this.config.roomWall.itemsByType.pipe(findInGroup('arAnchorStatus'), accumulateAndMapWallEvents('arAnchorStatus'), shareReplay(1));
        this.anchors = this.config.mediaController.data.messages.pipe(filter(msg => msg.type === 'arAnchors' && 'arAnchors' in msg), map(msg => msg.arAnchors?.anchors || []));
        this.elementGroups = this.config.roomWall.itemsByType.pipe(findInGroup('elementGroup'), accumulateAndMapWallEvents('elementGroup'), shareReplay(1));
        this.catalogItems = this.config.roomWall.itemsByType.pipe(findInGroup('catalogItem'), accumulateAndMapWallEvents('catalogItem'), shareReplay(1));
        this.remoteParticipantIsARCapable =
            config.mediaController.remoteParticipantCapabilities.pipe(map(capabilities => Boolean(capabilities?.canSendTrackableTypes &&
                capabilities.canSendTrackableTypes.length > 0)));
        // Get remote user video track wall items and check if their
        // camera subject has changed. If so, remove any related AR items.
        if (this.config.deleteLocalItemsOnRemoteCameraSubjectChange &&
            this.config.context.remoteUserId) {
            const remoteUserId = this.config.context.remoteUserId.toString();
            this.trackLocalArItems();
            this.config.roomWall.itemsByType
                .pipe(findInGroup('videoTrack'), accumulateAndMapWallEvents('videoTrack'), map(videoTrackWallItems => {
                const [latestTrack] = videoTrackWallItems
                    .filter(item => item.ownerUserId === remoteUserId)
                    .reverse();
                return latestTrack;
            }), takeWhile(response => response != null), distinctUntilKeyChanged('cameraSubject'))
                .subscribe(item => {
                logger.info('Video track camera subject changed. Removing related AR wall items', item?.id);
                this.deleteLocalArItems();
            });
        }
    }
    addScanningRequest(trackableDefinition) {
        invariant(this.config.context.remoteUserId && this.config.context.remoteDeviceId, 'Cannot add scanning request without remote user and device ids');
        const request = this.config.roomWall.builder.buildARScanningRequest({
            forUserId: this.config.context.remoteUserId.toString(),
            forDeviceId: this.config.context.remoteDeviceId.toString(),
            trackableDefinition,
        });
        logger.info(`Created AR Scanning Request for remoteUserId ${this.config.context.remoteUserId}`, request.id);
        this.config.roomWall.add(request);
        return request.id;
    }
    removeScanningRequest(id) {
        logger.info(`Removing AR Scanning Request for remoteUserId ${this.config.context.remoteUserId}`, id);
        this.config.roomWall.delete(id);
    }
    addAnchorRequest(trackableType, trackableId, anchorId, requestedPose) {
        invariant(this.config.context.remoteUserId && this.config.context.remoteDeviceId, 'Cannot add anchor request without remote user and device ids');
        const item = this.config.roomWall.builder.buildARAnchorRequest({
            forUserId: this.config.context.remoteUserId.toString(),
            forDeviceId: this.config.context.remoteDeviceId.toString(),
            trackableType,
            trackableId: new Uint8Array(trackableId.split('-').map(s => parseInt(s))),
            anchorId,
            requestedPose,
        });
        logger.info(`Adding AR Anchor Request for remoteUserId ${this.config.context.remoteUserId}`, item.id);
        this.config.roomWall.add(item);
    }
    addOrUpdateElementGroup(id, elementGroup) {
        const item = this.config.roomWall.builder.buildElementGroup(id, elementGroup);
        // Disabling this log message since it gets flodded by the new measurement tool. Need to find a balance here.
        // logger.info(
        //     `Adding / Updating Element Group Wall Item for remoteUserId ${this.config.context.remoteUserId}`,
        //     item.id,
        // );
        this.config.roomWall.addOrUpdate(item);
    }
    removeElementGroup(id) {
        this.config.roomWall.delete(id.toString());
    }
    addOrUpdateCatalogItem(id, catalogItem) {
        const item = this.config.roomWall.builder.buildCatalogItem(id, catalogItem);
        logger.info(`Adding / Updating Catalog Item WallItem for remoteUserId ${this.config.context.remoteUserId}`, item.id);
        this.config.roomWall.addOrUpdate(item);
    }
    trackLocalArItems() {
        invariant(this.config.context.localUserId, 'Expected localUserId to be defined when initializing ARController');
        const localUserId = this.config.context.localUserId.toString();
        const typesToRemove = [
            'arSource',
            'arAnchorRequest',
            'arAnchorStatus',
            'arScanningRequest',
            'arScanningStatus',
            'arTrackableStatus',
            'elementGroup',
            'catalogItem',
            'arSessionStatus',
        ];
        this.config.roomWall.itemsByType
            .pipe(filter(group => typesToRemove.includes(group.key ?? '')), accumulateAndMapWallEvents(), map((items) => items.filter(item => item.ownerUserId === localUserId)))
            .subscribe(items => {
            items.forEach(item => item.id && this.localArWallItems.add(item.id));
        });
    }
    async deleteLocalArItems() {
        logger.info('Deleting own AR items ', this.localArWallItems);
        this.localArWallItems.forEach(id => this.config.roomWall.delete(id));
        this.localArWallItems.clear();
    }
}
//# sourceMappingURL=ar_controller.js.map